/* CEG wishlist — client-side wishlist persisted in localStorage.
 * Public API: window.CEGWishlist.add(handle) / .remove(handle) / .has(handle) / .list() / .count() / .toggle(handle).
 * Fires `ceg:wishlist:updated` (CustomEvent) on every mutation.
 * Also wires:
 *   - any element with [data-ceg-wishlist-toggle="<handle>"] toggles that handle on click
 *     and gets `is-active` plus aria-pressed="true" when present in the wishlist.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'cegWishlist';

  function safeRead() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter((h) => typeof h === 'string' && h.length) : [];
    } catch (e) {
      console.warn('[CEGWishlist] read failed', e);
      return [];
    }
  }

  function safeWrite(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (e) {
      console.warn('[CEGWishlist] write failed', e);
    }
  }

  function emit() {
    const detail = { items: safeRead() };
    document.dispatchEvent(new CustomEvent('ceg:wishlist:updated', { detail }));
    paintToggles();
    paintCounts(detail.items.length);
  }

  function paintToggles() {
    const items = safeRead();
    document.querySelectorAll('[data-ceg-wishlist-toggle]').forEach((el) => {
      const h = el.getAttribute('data-ceg-wishlist-toggle');
      const active = !!h && items.indexOf(h) !== -1;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function paintCounts(n) {
    const count = typeof n === 'number' ? n : safeRead().length;
    document.querySelectorAll('[data-ceg-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('is-empty', count === 0);
    });
  }

  const CEGWishlist = {
    list() {
      return safeRead();
    },
    count() {
      return safeRead().length;
    },
    has(handle) {
      if (!handle) return false;
      return safeRead().indexOf(handle) !== -1;
    },
    add(handle) {
      if (!handle) return false;
      const items = safeRead();
      if (items.indexOf(handle) !== -1) return false;
      items.push(handle);
      safeWrite(items);
      emit();
      return true;
    },
    remove(handle) {
      if (!handle) return false;
      const items = safeRead();
      const i = items.indexOf(handle);
      if (i === -1) return false;
      items.splice(i, 1);
      safeWrite(items);
      emit();
      return true;
    },
    toggle(handle) {
      if (!handle) return false;
      return this.has(handle) ? (this.remove(handle), false) : (this.add(handle), true);
    },
    clear() {
      safeWrite([]);
      emit();
    },
  };

  // Delegated click handler for any toggle element on the page.
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target.closest('[data-ceg-wishlist-toggle]') : null;
    if (!t) return;
    e.preventDefault();
    const handle = t.getAttribute('data-ceg-wishlist-toggle');
    if (!handle) return;
    CEGWishlist.toggle(handle);
  });

  // Cross-tab sync.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) emit();
  });

  function init() {
    paintToggles();
    paintCounts();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CEGWishlist = CEGWishlist;
})();
